import React, { useState, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { devices } from '@vr/lib/styles/theme';
import { users } from '../helpers/api';
import { ToastCtx } from '../providers/ToastProvider';
import ForgotEmailForm from '../components/forms/ForgotEmailForm';
import { PageContainer, Container } from './common';
import Loader from '../components/Loader';
import OutlinedText from '../elements/OutlinedText';
import { Logo as DefaultLogo, Button, Text } from '../elements';
import messages from '../helpers/api/messages';
import { Modal } from '../components';
import { VRPAT } from '~/config/constants';
import { AppStoreLight, QRCodeDownload } from '~/assets/';

const textBase = css`
  color: #1b2126;
  letter-spacing: 0;
`;

const bodySmText = css`
  ${textBase}
  font-size: 14px;
  line-height: 17px;
`;

const bodyLgText = css`
  ${textBase}
  font-size: 18px;
  line-height: 22px;
`;

const titleMdText = css`
  ${textBase}
  font-size: 28px;
  font-weight: 700;
  line-height: 16px;
`;

const detailMdText = css`
  ${textBase}
  font-size: 10px;
  line-height: 12px;
`;

const Logo = styled(DefaultLogo)`
  display: none;

  @media (min-width: ${devices?.desktop}) {
    position: absolute;
    left: 64px;
    top: 21px;
    display: block;
  }
`;

const ModalEmails = styled(Modal)`
  .MuiPaper-root {
    max-width: 800px;
    border-radius: 0 24px;
    @media (max-width: ${({ theme }) => theme.devices.mobile}) {
      height: auto;
    }
  }
  .MuiDialog-container {
    align-items: center;

    @media (max-width: ${({ theme }) => theme.devices.mobile}) {
      align-items: flex-end;
    }
  }
`;

const MainBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  @media (max-width: ${({ theme }) => theme.devices.mobile}) {
    padding: 0 15px;
  }
`;

const ImageBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  margin-right: 20px;
  && img {
    max-width: 200px;
    max-height: 100px;
    object-fit: contain;
  }
`;

const DescriptionContainer = styled.div`
  margin-bottom: 20px;
  text-align: left;
  && ol {
    ${bodySmText}
    padding-left: 20px;
    margin-top: 10px;
    li {
      margin-bottom: 8px;
    }
  }
`;

const ModalEmailsContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  text-align: center;
  padding: 40px 80px;
  color: ${({ theme }) => theme.colors.gray400};
  @media (max-width: ${({ theme }) => theme.devices.mobile}) {
    padding: 30px 15px 15px 15px;
  }
  ${Text} {
    line-height: 36px;
    font-weight: 500;
  }
`;

const TitleDescription = styled.p`
  && {
    ${bodySmText}
  }
`;

const EmailSection = styled.div`
  text-align: center;
  padding: 20px 20px 0;
`;

const EmailLabel = styled.p`
  && {
    ${bodyLgText}
    margin-bottom: 8px;
    text-align: center;
  }
`;

const EmailValue = styled.p`
  && {
    ${titleMdText}
    margin-bottom: 20px;
  }
`;

const ImageCaption = styled.span`
  && {
    ${detailMdText}
    text-align: center;
  }
`;

export default function ForgotEmail() {
  const toast = useContext(ToastCtx);
  const history = useHistory();
  const logo = useSelector(state => state?.issuer?.logo);
  const issuer = useSelector(state => state?.issuer);
  const btnStyle = issuer.name === VRPAT ? 'institutional' : 'caixa';
  const isVR = issuer.name === VRPAT;

  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emails, setEmails] = useState([]);
  const handleSubmit = useCallback(
    async data => {
      try {
        setIsLoading(true);
        const response = await users.findEmailByCpf(
          data.cpf.replace(/\D/g, ''),
        );

        setEmails(response?.result?.emails || []);
        setShowModal(true);
      } catch (e) {
        toast.dispatch(messages(e, 'forgot_email'));
      } finally {
        setIsLoading(false);
      }
    },
    [toast],
  );

  return (
    <>
      <PageContainer>
        <Logo alt={logo?.alt} src={logo?.src} />
        <Container>
          <Loader isLoading={isLoading}>
            <ForgotEmailForm onSubmit={handleSubmit} />
          </Loader>
        </Container>
        {isVR && <OutlinedText onlyMobile>issoévr</OutlinedText>}
      </PageContainer>
      <ModalEmails open={showModal} fullWidth>
        <ModalEmailsContainer>
          <EmailSection>
            <EmailLabel>Seu e-mail cadastrado é:</EmailLabel>
            {emails.map(email => (
              <EmailValue key={email}>{email}</EmailValue>
            ))}
          </EmailSection>
          {isVR && (
            <MainBox>
              <ImageBox>
                <img
                  src={!isVR ? AppStoreLight : QRCodeDownload}
                  alt="Baixar o SuperApp VR"
                />
                <ImageCaption>Baixe o SuperApp VR</ImageCaption>
              </ImageBox>
              <DescriptionContainer>
                <TitleDescription>
                  Se não reconhece ou não tem mais este e-mail, faça a
                  recuperação do seu acesso pelo SuperApp VR.
                </TitleDescription>
                <ol>
                  <li>Acesse o SuperApp VR</li>
                  <li>Na tela inicial, vá em “Já tenho cadastro”</li>
                  <li>Depois, vá em “esqueci meu e-mail”</li>
                  <li>Informe seu CPF cadastrado na VR</li>
                  <li>
                    Siga as instruções para validar sua identidade e poder
                    atualizar seus dados com segurança
                  </li>
                </ol>
              </DescriptionContainer>
            </MainBox>
          )}
          <Button
            contrast
            label="Voltar para o início"
            aria-label="Voltar para o início"
            onClick={() => history.push('/login')}
            color={btnStyle}
          />
        </ModalEmailsContainer>
      </ModalEmails>
    </>
  );
}
